import type { Streak } from '@keel/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Award, CheckCircle2, ChevronRight, CircleHelp, Eye, EyeOff, Keyboard, LogOut, Monitor, Moon, Search, Settings, Sun,
} from 'lucide-react';
import { Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useTRPC } from '@/api/query';
import { StreakChip } from '@/components/engagement';
import {
  Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
  IconButton, Kbd,
} from '@/components/ui';
import { keyLabels } from '@/lib/keyboard';
import { usePreferences, useSession } from '@/lib/session';
import { useAppStore, type ThemePref } from '@/lib/store';
import { LogoMark, BetaTag } from './Logo';
import { usePageMeta, useRouteHandles } from './nav';
import { BRAND } from '@/brand';
import { TrialChip } from './Growth';

const THEME_ICON: Record<ThemePref, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };

const Breadcrumbs = () => {
  const { pathname } = useLocation();
  const meta = usePageMeta();
  const crumbs = useRouteHandles()
    .filter((c) => c.handle.crumb !== null)
    .map((c) => ({ pathname: c.pathname, label: c.handle.crumb ?? c.handle.title }));
  // pages inside an area's nested <Routes> add their own crumb (from PageHeader)
  if (meta && crumbs.length && meta.title !== crumbs[crumbs.length - 1]?.label) crumbs.push({ pathname, label: meta.title });
  // de-duplicate index routes that repeat their parent's title
  const unique = crumbs.filter((c, i) => i === 0 || c.label !== crumbs[i - 1]?.label);
  return (
    <nav aria-label="Breadcrumb" className="hidden min-w-0 sm:block">
      <ol className="flex items-center gap-1 text-sm">
        {unique.map((c, i) => {
          const last = i === unique.length - 1;
          const label = c.label;
          return (
            <Fragment key={i}>
              {i > 0 ? <ChevronRight className="size-3.5 shrink-0 text-text-muted" aria-hidden /> : null}
              <li className="min-w-0 truncate">
                {last ? (
                  <span aria-current="page" className="font-medium text-text">
                    {label}
                  </span>
                ) : (
                  <Link to={c.pathname} className="text-text-muted hover:text-text">
                    {label}
                  </Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

/** Top bar: breadcrumb, search (palette), check-in + streak, privacy, theme, help, user menu. */
export const TopBar = () => {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useSession();
  const { theme, privacyBlur, setPreference } = usePreferences();
  const set = useAppStore((s) => s.set);
  const openHelp = useAppStore((s) => s.openHelp);
  const streaks = useQuery(trpc.engagement.streaks.queryOptions(undefined, { meta: { silent: true }, staleTime: 60_000 }));
  const streakList: Streak[] | undefined = streaks.data;
  const daily = streakList?.find((s) => s.kind === 'daily');
  const logout = useMutation(
    trpc.auth.logout.mutationOptions({
      onSettled: () => {
        qc.clear();
        navigate('/login', { replace: true });
      },
    }),
  );
  const ThemeIcon = THEME_ICON[theme];
  const initials = (user?.displayName ?? '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <header data-print-hide className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-border bg-background/85 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-6">
      <Link to="/" className="md:hidden" aria-label={`${BRAND.name} home`}>
        <LogoMark className="size-7" />
      </Link>
      <BetaTag className="md:hidden" />
      <Breadcrumbs />
      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        <button
          type="button"
          data-tour="search"
          onClick={() => set({ paletteOpen: true })}
          className="hidden h-10 w-56 items-center gap-2 rounded-md border border-border bg-surface px-3 text-sm text-text-muted hover:border-border-strong/60 lg:flex"
        >
          <Search className="size-4" aria-hidden />
          <span className="flex-1 truncate whitespace-nowrap text-left">Search…</span>
          <span className="flex gap-0.5" aria-hidden>
            {keyLabels('mod+k')[0]?.map((k) => <Kbd key={k}>{k}</Kbd>)}
          </span>
        </button>
        <IconButton className="lg:hidden" aria-label="Search" icon={<Search aria-hidden />} onClick={() => set({ paletteOpen: true })} />
        <Button asChild variant="secondary" size="sm" className="hidden sm:inline-flex" data-tour="checkin">
          <Link to="/checkin">
            <CheckCircle2 aria-hidden />
            Check in
          </Link>
        </Button>
        <TrialChip />
        <StreakChip streak={daily} compact />
        <IconButton
          aria-label={privacyBlur ? 'Show amounts' : 'Hide amounts (privacy blur)'}
          aria-pressed={privacyBlur}
          icon={privacyBlur ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
          onClick={() => setPreference('privacyBlur', !privacyBlur)}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton aria-label={`Theme: ${theme}`} icon={<ThemeIcon aria-hidden />} tooltip={false} />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Theme</DropdownMenuLabel>
            {(['light', 'dark', 'system'] as const).map((t) => {
              const I = THEME_ICON[t];
              return (
                <DropdownMenuItem key={t} onSelect={() => setPreference('theme', t)}>
                  <I aria-hidden />
                  <span className="flex-1 capitalize">{t}</span>
                  {theme === t ? <span className="text-xs text-primary">Current</span> : null}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <IconButton aria-label="Help for this page" icon={<CircleHelp aria-hidden />} onClick={() => openHelp(null)} data-tour="help-button" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="ml-1 flex size-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary-hover"
            >
              <span aria-hidden>{initials}</span>
              <span className="sr-only">Account menu for {user?.displayName ?? 'you'}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60">
            <div className="px-2.5 py-2">
              <p className="truncate text-sm font-medium text-text">{user?.displayName}</p>
              <p className="truncate text-xs text-text-muted">{user?.email}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/settings/profile')}>
              <Settings aria-hidden /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/journey')}>
              <Award aria-hidden /> Journey & badges
            </DropdownMenuItem>
            <DropdownMenuItem className="coarse:hidden" onSelect={() => set({ shortcutsOpen: true })}>
              <Keyboard aria-hidden /> Keyboard shortcuts
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => logout.mutate()}>
              <LogOut aria-hidden /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
